import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api.js';
import { syncPreferences } from './preferences.js';

const AuthContext = createContext(null);

/**
 * Who's signed in, and their teams. `available` is false when the server
 * has no database (accounts switched off), so the UI can hide account
 * features instead of showing broken forms.
 */
export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null, teams: [], available: true });

  const refresh = useCallback(async () => {
    try {
      const me = await api('/auth/me');
      setState({ loading: false, user: me.user, teams: me.teams, available: true });
    } catch (err) {
      setState({ loading: false, user: null, teams: [], available: err.status !== 503 });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Preferences follow the account: load them on sign-in, stop saving
  // them remotely on sign-out (see preferences.js).
  const userId = state.user?.id ?? null;
  useEffect(() => {
    if (!state.loading) syncPreferences(userId ? { id: userId } : null);
  }, [userId, state.loading]);

  const value = useMemo(
    () => ({
      ...state,
      refresh,
      async login(email, password) {
        const me = await api('/auth/login', { method: 'POST', body: { email, password } });
        setState({ loading: false, user: me.user, teams: me.teams, available: true });
      },
      async register(email, password, displayName) {
        const me = await api('/auth/register', { method: 'POST', body: { email, password, displayName } });
        setState({ loading: false, user: me.user, teams: me.teams, available: true });
      },
      async updateProfile(patch) {
        const me = await api('/auth/me', { method: 'PATCH', body: patch });
        setState((s) => ({ ...s, user: me.user, teams: me.teams }));
      },
      async logout() {
        await api('/auth/logout', { method: 'POST' }).catch(() => {});
        setState((s) => ({ ...s, user: null, teams: [] }));
      },
    }),
    [state, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
